import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { NumberReveal, NumberRow, SmsRow } from '../api/types';
import { StatusBadge } from '../components/Badge';
import { Card, KeyValue, PageHeader } from '../components/Card';
import { CopyButton } from '../components/CopyButton';
import { EmptyState, ErrorBanner, LoadingBlock, WarningBanner } from '../components/Feedback';
import { Button, Field, Input, Select } from '../components/Form';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { DataTable, Mono } from '../components/Table';
import { useToast } from '../components/Toast';
import { ago, dateTime, money, num, text } from '../lib/format';

export function NumbersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [status, setStatus] = useState('');
  const [clientId, setClientId] = useState('');
  const [hasCode, setHasCode] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [revealModal, setRevealModal] = useState<{ id: number; tel: string } | null>(null);
  const [revealUrl, setRevealUrl] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (status) params.set('status', status);
    if (clientId) params.set('client_id', clientId);
    if (hasCode) params.set('has_code', hasCode);
    if (search) params.set('search', search);
    return `/admin-api/numbers?${params.toString()}`;
  }, [page, limit, status, clientId, hasCode, search]);

  const listQuery = useQuery<{ list: NumberRow[]; total: number }, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const smsPath = selectedId !== null ? `/admin-api/numbers/${selectedId}/sms` : null;
  const smsQuery = useQuery<{ list: SmsRow[] }, ApiError>({
    queryKey: ['api', smsPath ?? ''],
    queryFn: ({ signal }) => api.get<{ list: SmsRow[] }>(smsPath!, signal),
    enabled: selectedId !== null,
    retry: 1,
  });

  const doReveal = async (id: number, tel: string) => {
    setRevealModal({ id, tel });
    setRevealUrl(null);
    setRevealing(true);
    try {
      const result = await api.post<NumberReveal>(`/admin-api/numbers/${id}/reveal`);
      setRevealUrl(result.upstream_api_url);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
      setRevealModal(null);
    } finally {
      setRevealing(false);
    }
  };

  const list = listQuery.data?.list ?? [];
  const total = listQuery.data?.total ?? 0;
  const smsList = smsQuery.data?.list ?? [];

  return (
    <div className="space-y-4">
      <PageHeader title="Номера" description={`Всего: ${num(total)}`} />

      <Card title="Фильтры" bodyClassName="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <Field label="Статус">
            <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">Все</option>
              <option value="pool">В пуле</option>
              <option value="assigned">Выдан</option>
              <option value="expired">Истёк</option>
              <option value="released">Освобождён</option>
            </Select>
          </Field>
          <Field label="ID клиента">
            <Input value={clientId} onChange={(e) => { setClientId(e.target.value); setPage(1); }} inputMode="numeric" />
          </Field>
          <Field label="Наличие кода">
            <Select value={hasCode} onChange={(e) => { setHasCode(e.target.value); setPage(1); }}>
              <option value="">Любой</option>
              <option value="1">С кодом</option>
              <option value="0">Без кода</option>
            </Select>
          </Field>
          <Field label="Поиск">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(e.currentTarget.value.trim()); setPage(1); } }}
              placeholder="телефон"
            />
          </Field>
          <div className="flex items-end gap-2">
            <Button variant="primary" onClick={() => { setSearch(searchInput.trim()); setPage(1); }}>Найти</Button>
          </div>
          <div className="flex items-end">
            <Button variant="ghost" onClick={() => { setStatus(''); setClientId(''); setHasCode(''); setSearch(''); setSearchInput(''); setPage(1); }}>Сбросить</Button>
          </div>
        </div>
      </Card>

      <Card noPadding>
        {listQuery.error ? (
          <div className="p-3"><ErrorBanner message={listQuery.error.message} onRetry={() => listQuery.refetch()} /></div>
        ) : listQuery.isLoading && !list.length ? (
          <LoadingBlock />
        ) : list.length === 0 ? (
          <EmptyState title="Номера не найдены" />
        ) : (
          <>
            <DataTable<NumberRow>
              columns={[
                { key: 'tel', header: 'Телефон', width: '8rem', render: (r) => <Mono>{r.tel}</Mono> },
                { key: 'status', header: 'Статус', width: '5rem', render: (r) => <StatusBadge kind="number" status={r.status} /> },
                { key: 'project', header: 'Проект', hide: 'sm', render: (r) => <span className="truncate">{r.project}</span> },
                { key: 'client', header: 'Клиент', hide: 'md', render: (r) => <span className="truncate">{r.client ?? '—'}</span> },
                { key: 'cost', header: 'Стоимость', align: 'right', hide: 'lg', render: (r) => money(r.cost) },
                { key: 'sms_count', header: 'SMS', align: 'right', width: '4rem', render: (r) => num(r.sms_count) },
                { key: 'last_code', header: 'Код', width: '4rem', render: (r) => <Mono>{r.last_code ?? '—'}</Mono> },
                { key: 'end_time', header: 'Истекает', align: 'right', hide: 'xl', width: '7rem', render: (r) => ago(r.end_time) },
                { key: 'actions', header: '', width: '5rem', align: 'right', render: (r) => (
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setSelectedId(r.id)}>SMS</Button>
                    <Button size="sm" variant="ghost" onClick={() => doReveal(r.id, r.tel)}>Линк</Button>
                  </div>
                ) },
              ]}
              rows={list}
              rowKey={(r) => r.id}
              loading={listQuery.isFetching}
              minWidth="38rem"
            />
            <div className="border-t border-zinc-800 px-3 py-2 sm:px-4">
              <Pagination page={page} limit={limit} total={total} count={list.length} onPage={setPage} onLimit={(v) => { setLimit(v); setPage(1); }} />
            </div>
          </>
        )}
      </Card>

      {/* SMS history modal */}
      <Modal open={selectedId !== null} onClose={() => setSelectedId(null)} title="История SMS" size="md">
        {smsQuery.isLoading ? <LoadingBlock /> : smsQuery.error ? <ErrorBanner message={smsQuery.error.message} /> : (
          <DataTable<SmsRow>
            columns={[
              { key: 'code', header: 'Код', render: (r) => <Mono>{r.code ?? '—'}</Mono> },
              { key: 'code_time', header: 'Время кода', render: (r) => text(r.code_time) },
              { key: 'created_at', header: 'Создан', align: 'right', render: (r) => dateTime(r.created_at) },
            ]}
            rows={smsList}
            rowKey={(r) => r.id}
            emptyTitle="SMS-сообщений нет"
            minWidth="20rem"
          />
        )}
      </Modal>

      {/* Reveal link modal */}
      <Modal open={revealModal !== null} onClose={() => setRevealModal(null)} title="Upstream-ссылка" size="md">
        {revealModal ? (
          <div className="space-y-3">
            <WarningBanner
              title="Действие аудитируется"
              message="Каждое раскрытие upstream-ссылки записывается в журнал аудита. Используйте только для поддержки."
            />
            <KeyValue
              items={[
                { label: 'Телефон', value: <Mono>{revealModal.tel}</Mono> },
                { label: 'Upstream URL', value: revealing ? <span className="text-zinc-500">загрузка…</span> : <Mono>{revealUrl ?? '—'}</Mono> },
              ]}
            />
            {revealUrl ? (
              <div className="flex items-center gap-2">
                <CopyButton value={revealUrl} label="Копировать ссылку" />
                <a href={revealUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:underline">
                  Открыть
                </a>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
